import { Volume2, VolumeX } from "lucide-react";
import { useAudioMuted } from "../../hooks/useAudioMuted";
import { useT } from "../../i18n/i18nContext";

interface AudioMuteButtonProps {
  /**
   * "rail" — a compact icon button for the desktop side-rail foot.
   * "row"  — a full-width labelled row for the mobile "More" drawer.
   * "menu" — a labelled row inside the desktop trainer menu (GameTrainerMenu).
   */
  variant?: "rail" | "row" | "menu";
}

/**
 * Toggles all in-game sound + music on or off. Lets players who keep their own
 * music going silence the game (which also stops it claiming the iOS media
 * channel) without leaving the page. The preference is global and persisted —
 * see lib/audio/player.ts — so every surface reflects the same state.
 */
export function AudioMuteButton({ variant = "rail" }: AudioMuteButtonProps) {
  const { muted, toggle } = useAudioMuted();
  const t = useT();
  const Icon = muted ? VolumeX : Volume2;
  const label = muted ? t("navigation.sound.unmute") : t("navigation.sound.mute");

  if (variant === "row" || variant === "menu") {
    return (
      <button
        type="button"
        className={variant === "menu" ? "game-trainer-menu__row" : "game-more-sheet__row"}
        onClick={toggle}
        aria-pressed={muted}
      >
        <Icon size={18} strokeWidth={2} />
        <span>{muted ? t("navigation.sound.off") : t("navigation.sound.on")}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className="game-nav__mute-btn"
      onClick={toggle}
      aria-label={label}
      aria-pressed={muted}
      title={label}
    >
      <Icon size={20} strokeWidth={2} aria-hidden="true" />
    </button>
  );
}
